// @ts-nocheck
import React, { useState, useEffect, useCallback } from 'react';
import { Trash2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import apiFetch from '../../lib/supabaseClient';

interface TagUsage {
  tag_id: string;
  tag_name: string;
  usage_count: number;
}

const TagManager = () => {
  const { user } = useAuth();

  const [tags, setTags] = useState<TagUsage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editingTagName, setEditingTagName] = useState<string | null>(null);
  const [editingTagValue, setEditingTagValue] = useState('');
  const [isDeleteModeActive, setIsDeleteModeActive] = useState(false);

  // Fetch
  const fetchTags = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch('/tags/usage-counts');
      setTags(data || []);
    } catch (err) {
      console.error('Error fetching tags', err);
      setError('Failed to load tags. Please try again');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  // CRUD helpers
  const handleAddTag = async (newTagName: string) => {
    if (!newTagName || !user) return;
    if (newTagName.length > 25) {
      alert('Tag name cannot exceed 25 characters');
      return;
    }
    if (tags.some(t => t.tag_name.toLowerCase() === newTagName.toLowerCase())) {
      alert(`Tag "${newTagName}" already exists.`);
      return;
    }

    try {
      await apiFetch('/tags', { method: 'POST', body: { name: newTagName } });
      fetchTags();
    } catch (e) {
      console.error('Error adding tag', e);
      alert('Failed to add tag');
    }
  };

  const handleDeleteTag = async (tagName: string) => {
    const target = tags.find(t => t.tag_name === tagName);
    if (!target) return;
    if (target.usage_count > 0) return;

    try {
      await apiFetch(`/tags/${target.tag_id}`, { method: 'DELETE' });
      fetchTags();
    } catch (e) {
      console.error('Error deleting tag', e);
      alert('Failed to delete tag');
    }
  };

  const handleEditTagStart = (tagName: string) => {
    if (isDeleteModeActive) return;
    setEditingTagName(tagName);
    setEditingTagValue(tagName);
  };

  const handleCancelEdit = () => {
    setEditingTagName(null);
    setEditingTagValue('');
  };

  const handleSaveEdit = async () => {
    if (!editingTagName) return;
    const newName = editingTagValue.trim();
    if (!newName) return handleCancelEdit();
    if (newName === editingTagName) return handleCancelEdit();

    // duplicate check
    if (tags.some(t => t.tag_name.toLowerCase() === newName.toLowerCase())) {
      alert(`Tag "${newName}" already exists.`);
      return;
    }

    const target = tags.find(t => t.tag_name === editingTagName);
    if (!target) return handleCancelEdit();

    try {
      await apiFetch(`/tags/${target.tag_id}`, { method: 'PATCH', body: { name: newName } });
      fetchTags();
    } catch (e) {
      console.error('Error editing tag', e);
      alert('Failed to edit tag');
    } finally {
      handleCancelEdit();
    }
  };

  // UI
  return (
    <div className="content-area">
      <div className="content-header">
        <h2>Manage Tags</h2>
        <p className="content-description">View, add, edit, and manage your tags</p>
      </div>

      <div className="tags-management">
        <div className="add-tag-section">
          <input
            type="text"
            placeholder="New tag name"
            id="new-tag-input"
            className="input add-tag-input"
            disabled={isDeleteModeActive}
            maxLength={25}
          />
          <button
            className="button primary-button"
            onClick={() => {
              const input = document.getElementById('new-tag-input') as HTMLInputElement | null;
              if (input) {
                handleAddTag(input.value.trim());
                input.value = '';
              }
            }}
            disabled={isDeleteModeActive}
          >
            Add Tag
          </button>
          <button
            className={`button icon-button ${isDeleteModeActive ? 'active' : ''}`}
            onClick={() => setIsDeleteModeActive(!isDeleteModeActive)}
            title={isDeleteModeActive ? 'Exit Delete Mode' : 'Enter Delete Mode'}
          >
            <Trash2 size={18} />
          </button>
        </div>

        {loading && <p>Loading tags...</p>}
        {error && <p className="error-message">{error}</p>}

        {!loading && !error && (
          <ul className="tags-list">
            {tags.map(tag => {
              const isEditing = editingTagName === tag.tag_name;
              const isDeletable = isDeleteModeActive && tag.usage_count === 0;
              return (
                <li
                  key={tag.tag_id}
                  className={`tag-item ${isDeletable ? 'tag-item--deletable' : ''} ${isDeleteModeActive ? 'tag-item--delete-mode' : ''}`}
                  onClick={() => isDeletable && handleDeleteTag(tag.tag_name)}
                  title={isDeletable ? 'Click to delete' : (tag.usage_count > 0 ? `Used ${tag.usage_count} time(s)` : 'Click to edit')}
                >
                  {isEditing ? (
                    <input
                      type="text"
                      value={editingTagValue}
                      onChange={e => setEditingTagValue(e.target.value)}
                      onBlur={handleSaveEdit}
                      onKeyDown={e => {
                        if (e.key === 'Enter') handleSaveEdit();
                        else if (e.key === 'Escape') handleCancelEdit();
                      }}
                      autoFocus
                      className="tag-edit-input"
                    />
                  ) : (
                    <span onClick={() => !isDeleteModeActive && handleEditTagStart(tag.tag_name)}>
                      {tag.tag_name} ({tag.usage_count})
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default TagManager;
